// File: frontend/src/components/admin/AdminTraffic.tsx
//
// "Ziyaretçiler" tab — entry point to the server-side traffic report.
//
// Why this tab exists:
//   Google Analytics only counts visitors who accept the cookie banner. At this
//   site's traffic level Google does not model the rest either, because the
//   cookieless signals never reach the volume threshold its modelling requires.
//   The result is that most real visitors are invisible in GA — a day with
//   dozens of arrivals in the nginx log can show as a handful of users, or none.
//
//   The nginx access log has no such gap: every request is recorded regardless
//   of consent, ad blockers or whether JavaScript ran at all. GoAccess turns
//   that log into an HTML report, regenerated hourly by a cron job on the VPS.
//
// Why this is a link and not an embedded view:
//   The report lives outside the React app, at /istatistik, behind HTTP basic
//   auth handled by nginx. Embedding it in an iframe would trigger the browser's
//   credential prompt inside the panel, and the report would still be a separate
//   document. Opening it in a new tab keeps the auth flow normal.
//
// Setup for this tab to work is in ops/analytics/README.md — without it,
// /istatistik returns 404 and the button below leads nowhere.

import React from "react";
import { useTranslation } from "react-i18next";
import { FaChartLine, FaExternalLinkAlt, FaInfoCircle } from "react-icons/fa";
import "./AdminTraffic.css";

/** Path served by nginx from /var/www/3k1p-analytics, protected by basic auth. */
const REPORT_PATH = "/istatistik";

export const AdminTraffic: React.FC = () => {
    const { t } = useTranslation();

    // noopener/noreferrer on a new tab: the report is a static page on the same
    // origin, but the habit avoids handing it a window.opener reference.
    const openReport = () => {
        window.open(REPORT_PATH, "_blank", "noopener,noreferrer");
    };

    return (
        <div className="admin-traffic">
            <div className="admin-traffic__header">
                <h2 className="admin-traffic__title">
                    <FaChartLine />
                    {t("admin_traffic_title", "Ziyaretçiler")}
                </h2>
                <p className="admin-traffic__lead">
                    {t(
                        "admin_traffic_lead",
                        "Sunucu kayıtlarından üretilen gerçek ziyaretçi raporu. Çerez onayı, reklam engelleyici veya JavaScript durumundan etkilenmez."
                    )}
                </p>
            </div>

            <div className="admin-traffic__card">
                <button
                    type="button"
                    className="admin-traffic__button"
                    onClick={openReport}
                >
                    <FaExternalLinkAlt />
                    {t("admin_traffic_open", "Raporu Aç")}
                </button>
                <span className="admin-traffic__hint">
                    {t(
                        "admin_traffic_auth_hint",
                        "Yeni sekmede açılır ve kullanıcı adı/şifre sorar."
                    )}
                </span>
            </div>

            <div className="admin-traffic__note">
                <FaInfoCircle className="admin-traffic__note-icon" />
                <div>
                    <p>
                        {t(
                            "admin_traffic_note_refresh",
                            "Rapor her saat başı yeniden üretilir, bu yüzden son dakikaların verisi gecikmeli görünebilir."
                        )}
                    </p>
                    <p>
                        {t(
                            "admin_traffic_note_excluded",
                            "Yöneticiye ait IP adresleri ve bilinen arama motoru botları rapordan çıkarılmıştır; sayılar dışarıdan gelen gerçek ziyaretçileri gösterir."
                        )}
                    </p>
                </div>
            </div>
        </div>
    );
};